import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { IMAGES, WHATSAPP_URL, scrollToId, trackEvent, fbqContact, gaEvent } from "@/data/content";

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const scale = useTransform(scrollY, [0, 900], [1.08, 1.22]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img
          src={IMAGES.hero}
          alt="Carne d'autore al John Tartarino — ristorante di carne a Follonica"
          className="w-full h-full object-cover object-[50%_62%]"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0B] via-[#0D0C0B]/55 to-[#0D0C0B]/35" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D0C0B]/70 via-transparent to-transparent" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-28 pt-40 w-full"
      >
        <MaskedLine delay={0.35}>
          <span className="inline-flex items-center gap-3 text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#C5A059] mb-6">
            <span className="h-px w-10 bg-[#C5A059]/60" />
            Ristorante di carne &amp; cucina maremmana — Follonica, Toscana
          </span>
        </MaskedLine>

        <h1
          data-testid="hero-title"
          className="font-display font-medium text-[#F7F4EE] text-4xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight max-w-4xl"
        >
          <MaskedLine delay={0.5}>Carne, ma non solo</MaskedLine>
          <MaskedLine delay={0.65}>
            <span className="italic text-[#C5A059]">alla griglia.</span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={0.85}>
          <p className="mt-7 max-w-xl text-base sm:text-lg text-[#C5BCAD] leading-relaxed">
            Tartare, cotture lunghe a bassa temperatura, tagli alla griglia. Più la
            cucina della Maremma e una carta dei vini che ti tiene occupato un po'.
            Nel centro di Follonica.
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            data-testid="hero-whatsapp-cta"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => { fbqContact(); trackEvent("prenota_click"); gaEvent("click_whatsapp", { posizione: "hero" }); }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#7A1C2E] text-[#F7F4EE] text-sm uppercase tracking-[0.2em] hover:bg-[#9E2A3F] transition-colors duration-300"
          >
            Prenota su WhatsApp
          </a>
          <button
            data-testid="hero-menu-cta"
            onClick={() => scrollToId("#menu")}
            className="inline-flex items-center gap-2 px-8 py-4 border border-[#F7F4EE]/30 text-[#F7F4EE] text-sm uppercase tracking-[0.2em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors duration-300"
          >
            Scopri il menu
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#concept")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-[#C5A059]"
        aria-label="Scorri"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="block"
        >
          <ChevronDown size={26} strokeWidth={1.5} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
